import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Plus, FileText, Youtube, Trash2, Loader2, LogOut, Tv2, FilePlus2,
  LayoutDashboard, Compass, FolderOpen, Lightbulb, ChevronDown, ChevronRight, Check, BadgeCheck, RefreshCw, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function NavItem({ icon: Icon, label, onClick, active, testid, indent }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 rounded-md py-2 text-sm transition-colors ${
        indent ? "pl-9 pr-3" : "px-3"
      } ${active ? "bg-[#1d2028] text-[#f3f4f6]" : "text-[#9ca3af] hover:bg-[#14161c] hover:text-[#f3f4f6]"}`}
    >
      <Icon className={`h-4 w-4 shrink-0 ${active ? "text-[#00e5ff]" : ""}`} />
      <span className="truncate">{label}</span>
    </button>
  );
}

export default function Sidebar({ projects, channels = [], currentId, onNew, onSelect, onDelete, loading }) {
  const [confirm, setConfirm] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const startNew = (channel) => { setNewOpen(false); onNew(channel || null); };
  const handleNewClick = () => { channels.length > 0 ? setNewOpen(true) : onNew(null); };
  const soon = (name) => toast.info(`${name} is coming soon`);
  const onStudio = location.pathname === "/studio";
  const toggleGroup = (key) => setCollapsed((c) => ({ ...c, [key]: !c[key] }));

  const NONE = "__none__";
  const groups = {};
  for (const p of projects) {
    const key = p.channel_name || NONE;
    (groups[key] || (groups[key] = [])).push(p);
  }

  // Completion % across the pipeline: thumbnail + each scene image + each scene video + assembled video.
  const storyProgress = (p) => {
    const scenes = (p.scenes || []).length;
    if (!scenes) return (p.idea || p.title) ? 4 : 0; // small sliver for a fresh idea
    const imgs = (p.scene_image_ids || []).filter(Boolean).length;
    const vids = (p.scene_video_urls || []).filter(Boolean).length;
    const thumb = p.thumbnail_image_id ? 1 : 0;
    const assembled = p.has_final_video ? 1 : 0;
    const total = 1 + scenes + scenes + 1;
    const done = thumb + imgs + vids + assembled;
    return Math.max(4, Math.round((done / total) * 100));
  };
  const groupKeys = Object.keys(groups).sort((a, b) => {
    if (a === NONE) return 1;
    if (b === NONE) return -1;
    return a.localeCompare(b);
  });

  const renderStory = (p) => {
    const pct = storyProgress(p);
    const complete = pct >= 100;
    const published = !!p.published;
    // Published stories use YELLOW; completed-but-not-published use GREEN.
    const accent = published ? "#eab308" : "#22c55e";
    return (
    <div
      key={p.project_id}
      data-testid={`story-item-${p.project_id}`}
      title={`${pct}% complete${published ? " — Published" : complete ? " — includes assembled video" : ""}`}
      onClick={() => { if (!onStudio) navigate("/studio"); onSelect(p.project_id); }}
      className={`group relative flex cursor-pointer items-center gap-2 overflow-hidden rounded-md px-3 py-2 transition-colors ${
        currentId === p.project_id && onStudio ? "bg-[#1d2028] border border-[#00e5ff]/40" : "hover:bg-[#14161c] border border-transparent"
      }`}
    >
      <div
        data-testid={`story-progress-${p.project_id}`}
        className="pointer-events-none absolute inset-y-0 left-0 transition-[width] duration-700 ease-out"
        style={{ width: `${pct}%`, backgroundColor: `${accent}${complete || published ? "66" : "33"}` }}
        aria-hidden
      />
      <FileText className="relative z-10 h-4 w-4 shrink-0" style={{ color: (complete || published) ? accent : "#9ca3af" }} />
      <span className="relative z-10 flex-1 truncate text-sm">{p.title || p.idea || "Untitled story"}</span>
      {published ? (
        <BadgeCheck className="relative z-10 h-3.5 w-3.5 shrink-0" style={{ color: accent }} data-testid={`story-published-${p.project_id}`} />
      ) : complete ? (
        <Check className="relative z-10 h-3.5 w-3.5 shrink-0" style={{ color: accent }} />
      ) : null}
      <button
        data-testid={`delete-story-${p.project_id}`}
        onClick={(e) => { e.stopPropagation(); setConfirm(p); }}
        className="relative z-10 opacity-0 group-hover:opacity-100 text-[#9ca3af] hover:text-red-400 transition-opacity"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
    );
  };

  return (
    <aside className="flex h-full w-72 shrink-0 flex-col border-r border-[#272a35] bg-[#0e1015]">
      <div className="flex items-center gap-2 px-5 h-16 border-b border-[#272a35]">
        <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-md bg-white">
          <img src={`${process.env.PUBLIC_URL}/ytf-logo.png`} alt="YouTube Factory" className="h-full w-full object-contain" />
        </div>
        <span className="font-display font-bold tracking-tight">YouTube Factory <span className="text-[#2563eb]">AI</span></span>
      </div>

      {/* Primary navigation */}
      <div className="p-3 space-y-1">
        <NavItem icon={LayoutDashboard} label="Dashboard" testid="nav-dashboard" onClick={() => navigate("/dashboard")} active={location.pathname === "/dashboard"} />
        <NavItem icon={Compass} label="Niches" testid="nav-niches" onClick={() => navigate("/niches")} active={location.pathname === "/niches"} />
        <NavItem icon={Tv2} label="Channels" testid="nav-channels" onClick={() => navigate("/channels")} active={location.pathname === "/channels"} />
      </div>

      {/* Content group */}
      <div className="px-3">
        <div className="flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-[#f3f4f6]">
          <FolderOpen className="h-4 w-4 text-[#9ca3af]" /> Content
        </div>
        <NavItem icon={Lightbulb} label="Story Ideas" testid="nav-story-ideas" indent onClick={() => soon("Story Ideas")} />
        <NavItem icon={Sparkles} label="Funny Animals" testid="nav-funny-animals" indent onClick={() => navigate("/funny-animals")} active={location.pathname === "/funny-animals"} />
        <NavItem icon={RefreshCw} label="Regenerate" testid="nav-regenerate" indent onClick={() => navigate("/regenerate")} active={location.pathname === "/regenerate"} />
        <div className="flex items-center justify-between pl-9 pr-3 pt-2 pb-1">
          <span className="overline">Saved Stories</span>
          <button
            data-testid="new-story-btn"
            onClick={handleNewClick}
            title="New story"
            className="flex items-center gap-1 text-xs font-semibold text-[#00e5ff] hover:text-[#33ebff]"
          >
            <Plus className="h-3.5 w-3.5" /> New
          </button>
        </div>
      </div>

      {/* Saved stories list — grouped by channel */}
      <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1">
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-[#9ca3af]" />
          </div>
        ) : projects.length === 0 ? (
          <p className="px-2 py-4 text-sm text-[#9ca3af]">No stories yet. Create one to begin.</p>
        ) : (
          groupKeys.map((key) => (
            <div key={key} data-testid={`story-group-${key === NONE ? "none" : key}`}>
              <button
                onClick={() => toggleGroup(key)}
                data-testid={`story-group-toggle-${key === NONE ? "none" : key}`}
                className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium text-[#9ca3af] hover:text-[#f3f4f6]"
              >
                {collapsed[key] ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                {key === NONE ? <FileText className="h-3.5 w-3.5" /> : <Tv2 className="h-3.5 w-3.5 text-[#00e5ff]" />}
                <span className="flex-1 truncate text-left">{key === NONE ? "No channel" : key}</span>
                <span className="text-[10px] text-[#6b7280]">{groups[key].length}</span>
              </button>
              {!collapsed[key] && (
                <div className="space-y-1 pl-2">
                  {groups[key].map(renderStory)}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="border-[#272a35] bg-[#14161c] text-[#f3f4f6]" data-testid="new-story-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Start a new story</DialogTitle>
            <DialogDescription className="text-[#9ca3af]">
              Start blank, or begin from a channel to reuse its style, tone &amp; reference images.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <button
              data-testid="new-story-blank"
              onClick={() => startNew(null)}
              className="flex w-full items-center gap-3 rounded-md border border-[#272a35] bg-[#0e1015] px-4 py-3 text-left transition-colors hover:bg-[#1d2028]"
            >
              <FilePlus2 className="h-4 w-4 text-[#9ca3af]" />
              <span className="text-sm font-medium">Blank story</span>
            </button>
            {channels.map((c) => (
              <button
                key={c.channel_id}
                data-testid={`new-story-from-channel-${c.channel_id}`}
                onClick={() => startNew(c)}
                className="flex w-full items-center gap-3 rounded-md border border-[#272a35] bg-[#0e1015] px-4 py-3 text-left transition-colors hover:bg-[#1d2028]"
              >
                <Tv2 className="h-4 w-4 text-[#00e5ff]" />
                <span className="flex-1 min-w-0">
                  <span className="block truncate text-sm font-medium">{c.name || "Untitled channel"}</span>
                  <span className="block truncate text-xs text-[#9ca3af]">{(c.references || []).length} reference image(s)</span>
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="border-[#272a35] bg-[#14161c] text-[#f3f4f6]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete this story?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9ca3af]">
              “{confirm?.title || confirm?.idea || "Untitled story"}” and its generated images will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="cancel-delete-btn"
              className="rounded-md border-[#272a35] bg-transparent text-[#f3f4f6] hover:bg-[#1d2028] hover:text-[#f3f4f6]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={() => { onDelete(confirm.project_id); setConfirm(null); }}
              className="rounded-md bg-red-500 text-white hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="border-t border-[#272a35] p-4" data-testid="sidebar-user">
        <div className="flex items-center gap-3">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1d2028] text-[#00e5ff]">
              <Youtube className="h-4 w-4" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="truncate text-sm font-medium" data-testid="sidebar-user-name">{user?.name || "Signed in"}</p>
            <p className="truncate text-xs text-[#9ca3af]" data-testid="sidebar-user-email">{user?.email || ""}</p>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            title="Sign out"
            className="text-[#9ca3af] hover:text-red-400 transition-colors"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
